import { defineStore } from 'pinia'
import { http } from '@/helpers/http'
import { pipe } from '@/helpers/pipe'
import { ref } from 'vue'
import { track } from '@/helpers/mixpanelDes'
import { useDraftsStore } from '@/stores/drafts'
import { useLoaderStore } from '@/stores/loader'
import { useEditorStore } from '@/stores/editor'
import { useEditorImagesStore } from '@/stores/editor-images'
import { useRouter } from 'vue-router'
import { useSnackbarStore } from '@/stores/snackbar'
import {
  type TAIBaseResponse,
  type TAIChapterResponse,
  type TAIContent,
  type TAICreditsResponse,
  type TAIGenerateResponse,
  type TAIImageGenV2Response,
  type TAIKeywords,
  type TAIKeywordsResponse,
  type TAIModifyMode,
  type TAIModifyResponse,
  type TAINichesResponse,
  type TAIOutline,
  type TAIOutlineResponse,
  type TAIStatus,
  type TAIThemesResponse,
  type TAITitlesResponse,
} from '@/types/openai'
import type { TDraft } from '@/types/drafts'
import type { TWordGenieOperation } from '@/types/editor'
import type { TUserImage } from '@/types/images'
import { useCreatorStore } from './creator'
import { isWordgenieDraftUnfinished } from '@/helpers/wordgenieDraft'
import { createBrowserCache } from '@/helpers/browserCache'

export const useAIStore = defineStore('openai', () => {
  const snack = useSnackbarStore()
  const loader = useLoaderStore()
  const drafts = useDraftsStore()
  const editor = useEditorStore()
  const editorImages = useEditorImagesStore()
  const router = useRouter()
  const creator = useCreatorStore()
  const lastWordgenieOperation = ref<null | TWordGenieOperation | null>(null)
  const searchKeywordsCache = createBrowserCache<TAIKeywords | null>({
    name: 'search-keywords',
    version: 1,
    maxAgeMs: 48 * 60 * 60 * 1000,
    maxEntries: 100,
  })

  const status = ref<TAIStatus>({
    credits: null,
    tokenUsed: 0,
    error: null,
    cancel: false,
    draftId: null,
    generating: false,
    modifyTextMode: null,
    usedPromptText: '',
    totalCredits: 0,
  })

  const languages = [
    { name: 'English (British)', label: '🇬🇧 English (British)', locale: 'en-GB' },
    { name: 'English (US)', label: '🇺🇸 English (US)', locale: 'en-US' },
    { name: 'Dutch', label: '🇳🇱 Dutch', locale: 'nl-NL' },
    { name: 'French', label: '🇫🇷 French', locale: 'fr-FR' },
    { name: 'German', label: '🇩🇪 German', locale: 'de-DE' },
    { name: 'Hungarian', label: '🇭🇺 Hungarian', locale: 'hu-HU' },
    { name: 'Italian', label: '🇮🇹 Italian', locale: 'it-IT' },
    { name: 'Polish', label: '🇵🇱 Polish', locale: 'pl-PL' },
    { name: 'Portuguese', label: '🇵🇹 Portuguese', locale: 'pt-PT' },
    { name: 'Romanian', label: '🇷🇴 Romanian', locale: 'ro-RO' },
    { name: 'Spanish', label: '🇪🇸 Spanish', locale: 'es-ES' },
  ]

  const tones = [
    { name: 'Informative', label: '👮 Informative' },
    { name: 'Encouraging', label: '💪🏼 Encouraging' },
    { name: 'Constructive', label: '🔧 Constructive' },
    { name: 'Professional', label: '💼 Professional' },
    { name: 'Enthusiastic', label: '🤩 Enthusiastic' },
    { name: 'Humorous', label: '🤡 Humorous' },
    { name: 'Inspirational', label: '💫 Inspirational' },
    { name: 'Educational', label: '👩🏻‍🏫 Educational' },
    { name: 'Sincere', label: '😐️ Sincere' },
    { name: 'Playful', label: '😜 Playful' },
  ]

  const charactersLimit = 5000

  let generationController: AbortController | null = null

  function resetStatus() {
    status.value.tokenUsed = 0
    status.value.draftId = null
    status.value.generating = false
    status.value.error = null
    status.value.cancel = false
    status.value.modifyTextMode = null
    status.value.usedPromptText = ''
  }

  async function getCredits() {
    try {
      const data = await http.post<TAICreditsResponse>('/ai/credits')

      updateStatus(data)
      if (data?.status !== 'success') {
        return
      }

      const credits = data.result
      const extraCredits = credits.extra_ai_credits === 0 ? 0 : credits.extra_ai_credits - credits.used_extra_ai_credits
      const baseCredits = credits.max_ai_credits - credits.used_ai_credits

      status.value.credits = baseCredits + extraCredits
      status.value.totalCredits = credits.max_ai_credits + credits.extra_ai_credits
    } catch (e) {
      console.log('AI getCredits error:', e)
    }
  }

  async function modifyText(textToModify: string, mode: TAIModifyMode): Promise<string | null> {
    if (textToModify.length > charactersLimit) {
      snack.add('Selected text is too long. Please select less than 5000 characters.')
      return null
    }

    const removeQuotes = (s: string) => (s.startsWith('"') && s.endsWith('"') ? s.slice(1, -1) : s)
    const removeDot = (init: string) => (s: string) => (!init.endsWith('.') && s.endsWith('.') ? s.slice(0, -1) : s)

    try {
      const res = await http.post<TAIModifyResponse>('/modifyText', { textToModify, mode })
      if (res.status === 'error') {
        const msg =
          res.message === 'NOT_ENOUGH_CREDITS'
            ? 'Oops! It looks like you have run out of credits. Please contact support for more free credits.'
            : 'There was a problem generating the content, please try again in a moment'
        snack.add(msg, { permanent: true })
        return null
      } else {
        status.value.credits = res.creditsLeft ?? 0
        status.value.modifyTextMode = mode
        status.value.usedPromptText = res.usedPromptText
        return pipe(removeQuotes, removeDot(textToModify))(res.result) || null
      }
    } catch (e) {
      console.log('AI modifyText error:', e)
      return null
    }
  }

  async function generateText(prompt: string, refresh: boolean) {
    let res

    loader.show('Thinking...')
    try {
      res = await http.post<TAIGenerateResponse>('/ai/inline', { prompt, refresh })
    } catch (e) {
      console.log('AI generateText error:', e)
      res = null
    }
    loader.hide()

    if (!res) return null

    if (res.status !== 'success') {
      snack.add(res.message)
      return null
    }

    status.value.tokenUsed += res.totalTokenUsed ?? 0
    status.value.credits = res.creditsLeft ?? 0
    track('generated-ai-prompt', {
      phrase: prompt,
      //uuid: uuid,
      creditsUsed: res.totalTokenUsed,
      creditsLeft: res.creditsLeft,
      refresh,
    })
    return res.result.content
  }

  async function getNiches(niche: string, language: string) {
    let data

    loader.show('Thinking...')
    try {
      data = await http.post<TAINichesResponse>('/ai/subNiches', {
        niche,
        language,
      })
      if (data.status === 'success') {
        track('generated-ai-niches', {
          token_used: data.totalTokenUsed,
          niche,
          language,
        })
      } else {
        track('generated-ai-niches-failure', {
          msg: data.status,
          niche,
          language,
        })
      }
    } catch (e: any) {
      track('generated-ai-niches-failure', {
        msg: e.message,
        niche,
        language,
      })
      console.log('AI getNiches error:', e)
      data = null
    }
    loader.hide()

    updateStatus(data)
    return data?.status === 'success' ? [niche, ...data.result] : []
  }

  async function getTitles(niches: string[], audience: string, language: string) {
    let data

    loader.show('Thinking...')
    try {
      data = await http.post<TAITitlesResponse>('/ai/titles', { niches, audience, language })
      if (data.status === 'success') {
        track('generated-ai-title', {
          token_used: data.totalTokenUsed,
          audience,
          niches,
          language,
        })
      } else {
        track('generated-ai-title-failure', {
          msg: data.status,
          audience,
          niches,
          language,
        })
      }
    } catch (e: any) {
      track('generated-ai-title-failure', {
        msg: e.message,
        audience,
        niches,
        language,
      })
      console.log('AI getTitles error:', e)
      data = null
    }
    loader.hide()

    updateStatus(data)
    return data?.status === 'success' ? data.result : []
  }

  async function getThemes(title: string) {
    let data

    loader.show('Retrieving suggested themes...')
    try {
      data = await http.post<TAIThemesResponse>('/ai/themes', { title })
    } catch (e: any) {
      console.log('AI getThemes error:', e)
      data = null
    }
    loader.hide()
    return data?.status === 'success' ? data.result : []
  }

  async function generateMultipleImageV2(
    text: string,
    aspect: string,
    count: number,
    allowText = false,
  ): Promise<Awaited<TUserImage>[]> {
    let data

    try {
      data = await http.post<TAIImageGenV2Response>('/ai/generate_image_v2', { text, aspect, count, allowText })
      if (data.status === 'error') {
        const msg = data?.message ?? 'Error generating images!'
        snack.add(msg, { permanent: true, type: 'danger' })
      }
    } catch (e: any) {
      const msg = 'Error generating images!'
      snack.add(msg, { permanent: true, type: 'danger' })
      console.log('AI generateImage v2 error:', e)
      data = null
    }
    return data?.status === 'success' ? data?.images : []
  }

  async function getSearchKeywords(text: string, silent = false): Promise<TAIKeywords | null> {
    const key = text.trim()

    if (!key) return null

    return searchKeywordsCache.remember(key, () => fetchSearchKeywords(key, silent))
  }

  function cachedSearchKeywords(text: string): TAIKeywords | null {
    const key = text.trim()

    return key ? searchKeywordsCache.get(key) : null
  }

  async function fetchSearchKeywords(text: string, silent: boolean): Promise<TAIKeywords | null> {
    if (!silent) loader.show('Thinking...')

    try {
      const data = await http.post<TAIKeywordsResponse>(
        '/ai/keywords',
        {
          text,
        },
        false,
        5000,
      )
      return data?.status === 'success' ? data.result : null
    } catch {
      return null
    } finally {
      if (!silent) loader.hide()
    }
  }

  async function getKeywords(text: string, silent = false): Promise<string> {
    return (await getSearchKeywords(text, silent))?.query ?? ''
  }

  async function getOutline(
    niches: string[],
    audience: string,
    title: string,
    language: string,
    tone: string,
    tags: number[],
  ) {
    let data
    loader.show('Thinking...')
    try {
      data = await http.post<TAIOutlineResponse>('/ai/outline', {
        niches,
        audience,
        title,
        language,
        tone,
      })
      if (data.status === 'success') {
        track('generated-ai-outline', {
          token_used: data.totalTokenUsed,
          niches,
          audience,
          title,
          language,
          tone,
        })
      } else {
        track('generated-ai-outline-failure', {
          msg: data.status,
          niches,
          audience,
          title,
          language,
          tone,
        })
      }
    } catch (e: any) {
      track('generated-ai-outline-failure', {
        msg: e.message,
        niches,
        audience,
        title,
        language,
        tone,
      })
      console.log('AI getOutline error:', e)
      data = null
    }
    loader.hide()

    updateStatus(data)

    if (data?.status === 'success') {
      saveContentAsDraft(title, data.result, niches, audience, language, tone, tags)
      return data.result
    } else {
      return []
    }
  }

  function insertContentByTitleAndSubtitle(content: string, chapterTitle: string, subChapterTitle: string) {
    const editorInstance = editor.getEditor()
    if (!editorInstance) return

    const view = editorInstance.view
    const { state } = view
    const { doc } = state

    doc.descendants((node, from) => {
      if (
        node.type.name === 'paragraph' &&
        node.attrs.wordgenieTitle === chapterTitle &&
        node.attrs.wordgenieSubtitle === subChapterTitle
      ) {
        const newHtml =
          '\n\n' +
          content
            .trim()
            .split(/\n\n/)
            .map((paragraph) => paragraph.replace(/\n/g, '<br>'))
            .join('')

        handleScroll(() => editorInstance.commands.insertContentAt({ from, to: from + node.nodeSize }, newHtml))

        return false
      }
      return true
    })
  }

  function handleScroll(callback: () => void) {
    const windowY = window.scrollY
    callback()

    const scrollHandler = () => {
      windowY && window.scrollTo({ top: windowY, behavior: 'instant' })
      setTimeout(() => window.removeEventListener('scroll', scrollHandler), 100)
    }

    window.addEventListener('scroll', scrollHandler)
  }

  function isLastParagraphWithTitleAndSubtitle(chapterTitle: string, subChapterTitle: string): boolean {
    const view = editor.getEditor()?.view
    if (!view) return false

    const { doc } = view.state
    let inChapter = false
    let lastSubtitle: string | null = null

    doc.descendants((node) => {
      if (node.type.name === 'heading' && node.attrs.level === 2 && node.textContent === chapterTitle) {
        inChapter = true
        return true
      }
      if (inChapter && node.type.name === 'heading' && node.attrs.level === 2) {
        inChapter = false
        return false
      }
      if (inChapter && node.type.name === 'paragraph') {
        lastSubtitle = node.attrs.wordgenieSubtitle || null
      }
      return true
    })

    return lastSubtitle === subChapterTitle
  }

  async function getChapter(
    niches: string[],
    audience: string,
    title: string,
    chapterTitle: string,
    subChapterTitle: string,
    language: string,
    tone: string,
    prompt?: string,
    isLastParagraph = false,
    signal?: AbortSignal,
  ) {
    let data
    try {
      data = await http.post<TAIChapterResponse>(
        '/ai/subChapterGen',
        {
          niches,
          audience,
          title,
          chapterTitle,
          subChapterTitle,
          language,
          tone,
          ...(prompt ? { prompt } : {}),
          isLastParagraph,
          generateImages: drafts.draftFlags?.wordgenie_generate_images ?? creator.generateImages,
        },
        false,
        0,
        signal,
      )
      if (data.status === 'success') {
        track('generated-ai-content-chapter', {
          token_used: data.totalTokenUsed,
          title,
          chapterTitle,
          subChapterTitle,
          audience,
          niches,
        })
      } else {
        const msg = data.message ?? 'Error generating sub chapter content, status: ' + data.status
        throw new Error(msg)
      }
    } catch (e: any) {
      if (signal?.aborted) throw e
      track('generated-ai-content-chapter-failure', {
        msg: e.message,
        title,
        chapterTitle,
        subChapterTitle,
        audience,
        niches,
      })
      data = null
      throw e
    }

    updateStatus(data)

    return data?.status === 'success'
      ? {
          content: data.result.content
            .trim()
            .split(/\n\n/)
            .map((paragraph) => {
              return `<p data-wordgenie-keywords="${data.result.keywords}">${paragraph.replace(/\n/g, '<br>')}</p>`
            })
            .join(''),
          keywords: data.result.keywords,
          description: data.result.description,
        }
      : { content: '', description: '', keywords: [] }
  }

  async function getChapterByObject(
    {
      niches,
      audience,
      title,
      chapterTitle,
      subChapterTitle,
      language,
      tone,
      prompt,
      isLastParagraph = false,
    }: {
      niches: string[]
      audience: string
      title: string
      chapterTitle: string
      subChapterTitle: string
      language: string
      tone: string
      prompt?: string
      isLastParagraph?: boolean
    },
    signal?: AbortSignal,
  ) {
    return getChapter(
      niches,
      audience,
      title,
      chapterTitle,
      subChapterTitle,
      language,
      tone,
      prompt,
      isLastParagraph,
      signal,
    )
  }

  function fallbackP(title: string, subtitle?: string, prompt?: string) {
    const escapeHTML = (t: string) => {
      const e = document.createElement('div')
      e.textContent = t
      return e.innerHTML
    }

    return `<p
      data-wordgenie-subtitle="${escapeHTML(subtitle ?? title)}"
      data-wordgenie-title="${escapeHTML(title)}"
      ${prompt ? `data-wordgenie-prompt="${escapeHTML(prompt)}"` : ''}></p>`
  }

  async function saveContentAsDraft(
    title: string,
    content: TAIContent[] | TAIOutline[],
    niches: string[],
    audience: string,
    language: string,
    tone: string,
    tags: number[],
    openDoc = false,
    generateImages = false,
  ) {
    const html = content.reduce(
      (prevH, group, index) => `${prevH}
      ${index > 0 ? '<div data-type="pageBreak"></div>' : ''}<h2>${group.title}</h2>
      ${!group?.children?.length ? ('content' in group ? group.content : fallbackP(group.title, undefined, group.prompt)) : ''}
      ${group?.children?.reduce(
        (prevP, child) => `${prevP}
        <h3>${child.title}</h3>
        ${'content' in child ? child.content : fallbackP(group.title, child.title, child.prompt)}
      `,
        '',
      )}
    `,
      '',
    )
    const source = JSON.stringify({ title, audience, niches, language, tone })

    return drafts
      .save(status.value.draftId, title, html, '', { type: 'from-wordgenie' }, source, tags)
      .then((res: number) => {
        status.value.draftId = res
        creator.generateImages = !!(drafts?.draftFlags?.['wordgenie_generate_images'] ?? generateImages)
        drafts.setFlags(res, { wordgenie_generate_images: creator.generateImages })
        let routeName = 'ebook'
        if (creator.isWordgeniePrintbookImport) {
          routeName = 'print-book'
        } else if (creator.isWordgenieKindleImport) {
          routeName = 'kindle-book'
        }
        openDoc && router.push(`/docs/${routeName}/${status.value.draftId}`)
      })
  }

  async function getFullContent(
    niches: string[],
    audience: string,
    title: string,
    content: TAIContent[],
    language: string,
    tone: string,
    tags: number[],
  ) {
    const controller = new AbortController()
    const { signal } = controller
    generationController = controller
    status.value.generating = true
    // TEST to check edit during generation
    // loader.showCustom(() => import('@/components/creator/DesCreatorLoader.vue'))

    const updateStates = async (done: boolean = false) => {
      const generateImages = !!(drafts?.draftFlags?.['wordgenie_generate_images'] ?? creator.generateImages)
      await saveContentAsDraft(title, content, niches, audience, language, tone, tags, generateImages).then(() => {
        if (status.value.draftId) {
          if (status.value.cancel || done) {
            status.value.generating = false
            if (!status.value.cancel && !status.value.error) {
              snack.add('Your eBook is ready — dive in and check it out!', { type: 'success' })
            }
            // TEST to check edit during generation
            // loader.hide()
          }
          /*
          drafts.get(status.value.draftId).finally(() => {
            if (status.value.cancel || done) {
              status.value.generating = false
              loader.hide()
            }
          })
          */
        }
      })
      editorImages.checkPlaceholders()

      return status.value.cancel
    }
    try {
      for (const h2 of content) {
        if (h2.children?.length) {
          for (const h3 of h2.children) {
            const chapter = await getChapter(
              niches,
              audience,
              title,
              h2.title,
              h3.title,
              language,
              tone,
              h3.prompt,
              isLastParagraphWithTitleAndSubtitle(h2.title, h3.title),
              signal,
            )

            insertContentByTitleAndSubtitle(chapter.content, h2.title, h3.title)

            h3.content = chapter.content
            if ((await updateStates()) || signal.aborted) {
              return
            }
          }
        } else {
          const chapter = await getChapter(
            niches,
            audience,
            title,
            h2.title,
            h2.title,
            language,
            tone,
            h2.prompt,
            false,
            signal,
          )
          insertContentByTitleAndSubtitle(chapter.content, h2.title, h2.title)
          h2.content = chapter.content
          if ((await updateStates()) || signal.aborted) {
            return
          }
        }
      }
      await updateStates(true)
    } catch (e) {
      if (signal.aborted) {
        return
      }

      throw e
    } finally {
      if (generationController === controller) {
        generationController = null
      }
    }
  }

  function abortGeneration() {
    generationController?.abort()
  }

  function updateStatus(data: TAIBaseResponse<any> | null) {
    if (!data) {
      status.value.error = 'The server did not respond, please try again in a moment'
    } else if (data.status === 'error') {
      if (data.message === 'NOT_ENOUGH_CREDITS') {
        status.value.error =
          'Oops! It looks like you have run out of credits. Please contact support for more free credits.'
      } else {
        status.value.error = 'There was a problem generating the content, please try again in a moment'
      }
    } else if (data.status === 'moderation_error') {
      status.value.error = data.message
    } else if (data.status === 'success') {
      status.value.credits = data.creditsLeft ?? 0
      status.value.tokenUsed += data.totalTokenUsed ?? 0
      status.value.error = null
    }
  }

  function isUnfinished(draft: Partial<TDraft> | null): boolean {
    return isWordgenieDraftUnfinished(draft)
  }

  async function getDraftKeywords(draftId?: number): Promise<string[] | null> {
    if (!draftId) {
      return null
    }

    try {
      const res = await http.get<TAIKeywordsResponse>('/ai/draft-keywords/' + draftId)

      if (!res || res.status !== 'success' || !res.result.keywords.length) {
        return null
      }

      return res.result.keywords
    } catch {
      return null
    }
  }

  return {
    languages,
    status,
    tones,
    lastWordgenieOperation,
    resetStatus,
    generateText,
    getCredits,
    getFullContent,
    abortGeneration,
    getKeywords,
    getSearchKeywords,
    cachedSearchKeywords,
    getDraftKeywords,
    getOutline,
    getNiches,
    getTitles,
    getThemes,
    getChapter,
    getChapterByObject,
    modifyText,
    saveContentAsDraft,
    isUnfinished,
    generateMultipleImageV2,
    insertContentByTitleAndSubtitle,
    isLastParagraphWithTitleAndSubtitle,
  }
})
